import React, { useEffect, useRef, useState } from 'react';

// bootstrap
import { Button, ProgressBar } from 'react-bootstrap';

// custom components
import { CustomSpinner } from '_atoms';
import ToolTippy from '../tooltip/toolTippy';

// icons
import { IoCloudUploadOutline, IoDownloadOutline, IoCheckmarkCircle, IoAlertCircle, IoClipboardOutline, IoInformationCircleOutline } from 'react-icons/io5';

// utils
import {
    readBookingFile,
    readPastedRows,
    validateBookingRows,
    buildBookingDocument,
    buildTemplateCsv,
    buildTemplateWorkbookBlob,
    downloadBlob,
    downloadCsv,
    flightLabelOf,
    MAX_BULK_ROWS,
    BULK_TEMPLATE_COLUMNS,
} from '_utils/bulk-booking-csv';

// styles
import { Colors } from '_styles';
import { useIsMobile } from '_utils/hooks';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectIata, selectUserDb } from '_redux/slices/main-screen/user';
import {
    bulkCreateBookings,
    resetBulkUpload,
    selectBulkUploadStatus,
    selectBulkUploadResults,
    selectBulkUploadError,
    selectBulkUploadProgress,
} from '_redux/slices/timeline/timeline-bookings';
import { patchBookingList } from '_redux/slices/prm-bookings/bookings';

// The only file formats the browser can open reliably. The file input is limited to
// these; anything else the operator has (Numbers, Google Sheets) goes through the
// paste option below, which works from the clipboard regardless of the source app.
const SUPPORTED_EXTENSIONS = ['csv', 'xlsx'];

// Formats that look like spreadsheets but cannot be opened as files. Drag-and-drop
// bypasses the input's accept filter, so these still need a specific message.
const UNSUPPORTED_HINTS = {
    numbers: 'Numbers only ever saves in its own format, so Save always produces a .numbers document. To get an uploadable file, open the document in Numbers and use the File menu at the very top of the screen → Export To → CSV… (this is not in the Finder right-click menu), then upload the exported file.',
    pages: 'This is a Pages document, not a spreadsheet. Upload a CSV or Excel file, or paste the rows below.',
    pdf: 'PDF files cannot be read. Upload the CSV or Excel version of this list, or paste the rows below.',
    docx: 'Word documents cannot be read. Upload a CSV or Excel file, or paste the rows below.',
    doc: 'Word documents cannot be read. Upload a CSV or Excel file, or paste the rows below.',
    xls: 'This is an older Excel file. Open it and save as .xlsx or .csv, then upload that.',
};

const extensionOf = (name) => {
    const parts = String(name || '').toLowerCase().split('.');
    return parts.length > 1 ? parts.pop() : '';
};

const sectionCard = {
    background: '#fff',
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: 12,
    padding: '24px 28px',
    marginBottom: 20,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

const sectionHeader = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 20,
};

const stepBadge = {
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: Colors.PRM_LIGHT_BLUE,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 600,
    flexShrink: 0,
};

const sectionTitle = {
    fontSize: 16,
    fontWeight: 600,
    color: Colors.TEXT_COLOR,
    margin: 0,
};

const BulkUploadForm = () => {
    // phones: tighter card padding
    const isMobile = useIsMobile();
    const cardStyle = isMobile ? { ...sectionCard, padding: 16 } : sectionCard;

    //#region state
    const [file, setFile] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [parseError, setParseError] = useState('');
    const [unknownHeaders, setUnknownHeaders] = useState([]);
    const [validation, setValidation] = useState(null);   // { rows, validCount, errorCount, flights }
    const [dragActive, setDragActive] = useState(false);
    const [source, setSource] = useState('file');       // 'file' | 'paste'
    const [pastedText, setPastedText] = useState('');
    const [assumedColumnOrder, setAssumedColumnOrder] = useState(false);
    const fileInputRef = useRef(null);
    //#endregion

    //#region redux
    const dispatch = useDispatch();
    const iata = useSelector(selectIata);
    const userDb = useSelector(selectUserDb);
    const isAirline = userDb?.details?.type === 'AIRLINE';
    const authority = userDb?.details?.authority;

    const uploadStatus = useSelector(selectBulkUploadStatus);
    const uploadResults = useSelector(selectBulkUploadResults);
    const uploadError = useSelector(selectBulkUploadError);
    const uploadProgress = useSelector(selectBulkUploadProgress);
    //#endregion

    useEffect(() => {
        dispatch(resetBulkUpload());
        return () => { dispatch(resetBulkUpload()); };
    }, []);

    // confirm every booking that was created, matching the single-request flow
    useEffect(() => {
        if (uploadStatus !== 'succeeded') return;

        const created = (uploadResults || [])
            .filter((result) => result.status === 'created' && result.booking && result.booking._id)
            .map((result) => ({ ...result.booking, status: 'user-confirmed' }));

        if (created.length > 0) dispatch(patchBookingList(created));
    }, [uploadStatus]);

    //#region handlers
    const resetAll = () => {
        setFile(null);
        setPastedText('');
        setParseError('');
        setUnknownHeaders([]);
        setAssumedColumnOrder(false);
        setValidation(null);
        dispatch(resetBulkUpload());
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Shared by the file and paste routes — everything after the rows are extracted.
    const applyParsedRows = ({ rows, unknownHeaders: unknown, assumedColumnOrder: assumed }) => {
        if (rows.length > MAX_BULK_ROWS) {
            throw new Error(
                `This contains ${rows.length} rows. Please split it into batches of ${MAX_BULK_ROWS} rows or fewer.`
            );
        }

        const result = validateBookingRows(rows, { authority, isAirline, iata });

        if (result.rows.length === 0) {
            throw new Error('No passenger rows were found.');
        }

        setUnknownHeaders(unknown || []);
        setAssumedColumnOrder(Boolean(assumed));
        setValidation(result);
    };

    const handlePaste = () => {
        setParseError('');
        setValidation(null);
        setUnknownHeaders([]);
        setAssumedColumnOrder(false);
        setFile(null);
        dispatch(resetBulkUpload());

        try {
            applyParsedRows(readPastedRows(pastedText));
        } catch (error) {
            setParseError(error?.message || 'The pasted rows could not be read.');
            setValidation(null);
        }
    };

    const handleFile = async (selected) => {
        if (!selected) return;

        setFile(selected);
        setPastedText('');
        setParseError('');
        setValidation(null);
        setUnknownHeaders([]);
        setAssumedColumnOrder(false);
        dispatch(resetBulkUpload());

        const extension = extensionOf(selected.name);

        if (UNSUPPORTED_HINTS[extension]) {
            setParseError(UNSUPPORTED_HINTS[extension]);
            return;
        }

        if (extension && !SUPPORTED_EXTENSIONS.includes(extension)) {
            setParseError(
                `"${selected.name}" is not a spreadsheet file. Please upload a CSV or Excel file (${SUPPORTED_EXTENSIONS
                    .map((e) => `.${e}`)
                    .join(', ')}).`
            );
            return;
        }

        setParsing(true);

        try {
            applyParsedRows(await readBookingFile(selected));
        } catch (error) {
            setParseError(error?.message || 'The file could not be read.');
            setValidation(null);
        } finally {
            setParsing(false);
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setDragActive(false);
        const dropped = event.dataTransfer?.files?.[0];
        if (dropped) handleFile(dropped);
    };

    const handleSubmit = () => {
        if (!validation || validation.validCount === 0) return;

        const bookings = validation.rows
            .filter((row) => row.isValid)
            .map((row) => ({
                rowNumber: row.__rowNumber,
                name: `${row.firstName} ${row.secondName}`,
                flightLabel: flightLabelOf(row),
                document: buildBookingDocument(row),
            }));

        dispatch(bulkCreateBookings({ bookings, iata, concurrency: 4 }));
    };

    const downloadTemplate = () => downloadCsv('PRM_Assist_Bulk_Upload_Template.csv', buildTemplateCsv());

    const downloadTemplateWorkbook = () =>
        downloadBlob('PRM_Assist_Bulk_Upload_Template.xlsx', buildTemplateWorkbookBlob());

    //#endregion

    const createdCount = (uploadResults || []).filter((r) => r.status === 'created').length;
    const failedResults = (uploadResults || []).filter((r) => r.status === 'failed');

    //#region results view
    if (uploadStatus === 'succeeded') {
        return (
            <div>
                <div style={cardStyle}>
                    <div style={sectionHeader}>
                        <div style={{ ...stepBadge, backgroundColor: failedResults.length === 0 ? Colors.DARK_GREEN : Colors.PRM_LIGHT_BLUE }}>✓</div>
                        <h3 style={sectionTitle}>Upload Complete</h3>
                    </div>

                    <div style={styles.statRow}>
                        <div style={{ ...styles.statCard, borderColor: Colors.DARK_GREEN }}>
                            <span style={{ ...styles.statNumber, color: Colors.DARK_GREEN }}>{createdCount}</span>
                            <span style={styles.statLabel}>Requests created</span>
                        </div>
                        <div style={{ ...styles.statCard, borderColor: failedResults.length > 0 ? Colors.DARK_RED : Colors.GRAY_MEDIUM }}>
                            <span style={{ ...styles.statNumber, color: failedResults.length > 0 ? Colors.DARK_RED : Colors.GRAY_DARK }}>{failedResults.length}</span>
                            <span style={styles.statLabel}>Rows not created</span>
                        </div>
                    </div>

                    {failedResults.length > 0 && (
                        <>
                            <p style={styles.helpText}>
                                The rows below were not created. Correct them in your file and upload again — the requests
                                that succeeded are already live and will not be duplicated.
                            </p>
                            <div style={styles.tableWrap}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Row</th>
                                            <th style={styles.th}>Passenger</th>
                                            <th style={styles.th}>Flight</th>
                                            <th style={styles.th}>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {failedResults.map((result) => (
                                            <tr key={result.rowNumber} style={{ background: Colors.LIGHT_RED }}>
                                                <td style={styles.td}>{result.rowNumber}</td>
                                                <td style={styles.td}>{result.name}</td>
                                                <td style={styles.td}>{result.flightLabel}</td>
                                                <td style={{ ...styles.td, color: Colors.DARK_RED }}>{result.message}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    <div style={{ marginTop: 20 }}>
                        <Button onClick={resetAll} style={styles.primaryButton}>Upload Another File</Button>
                    </div>
                </div>
            </div>
        );
    }
    //#endregion

    return (
        <div>
            <ToolTippy selectors={['[data-tippy-content]']} />

            {/* Step 1 — template */}
            <div style={cardStyle}>
                <div style={sectionHeader}>
                    <div style={stepBadge}>1</div>
                    <h3 style={sectionTitle}>Download the Template</h3>
                </div>
                <p style={styles.helpText}>
                    Fill in the template with your passengers — one passenger per row. Flight details are taken from
                    each row, so a single file can cover several flights. Maximum {MAX_BULK_ROWS} rows.
                </p>
                <div style={styles.columnList}>
                    {BULK_TEMPLATE_COLUMNS.map((column) => (
                        <span key={column} style={styles.columnChip}>
                            {column}
                            {column === 'Reference Number' && <span style={styles.optionalTag}>optional</span>}
                        </span>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <Button variant="outline-primary" onClick={downloadTemplateWorkbook} style={styles.secondaryButton}>
                        <IoDownloadOutline size={16} style={{ marginRight: 8 }} />
                        Download Excel Template
                    </Button>
                    <Button variant="outline-secondary" onClick={downloadTemplate} style={styles.secondaryButton}>
                        <IoDownloadOutline size={16} style={{ marginRight: 8 }} />
                        Download CSV Template
                    </Button>
                </div>


            </div>

            {/* Step 2 — upload or paste */}
            <div style={cardStyle}>
                <div style={sectionHeader}>
                    <div style={stepBadge}>2</div>
                    <h3 style={sectionTitle}>Add Your Passengers</h3>
                </div>

                {/* One picker, shared by the tab and the drop zone */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.numbers"
                    onChange={(e) => handleFile(e.target.files && e.target.files[0])}
                    style={{ display: 'none' }}
                />

                {/* Source switcher — a file, or rows copied from any spreadsheet app */}
                <div style={styles.sourceBar} role="tablist">
                    <button
                        role="tab"
                        aria-selected={source === 'file'}
                        onClick={() => {
                            setSource('file');
                            // the tab itself opens the picker, so it is not just a label
                            if (fileInputRef.current) fileInputRef.current.click();
                        }}
                        style={{ ...styles.sourceTab, ...(source === 'file' ? styles.sourceTabActive : {}) }}
                    >
                        <IoCloudUploadOutline size={15} />
                        <span>Upload a file</span>
                    </button>
                    <button
                        role="tab"
                        aria-selected={source === 'paste'}
                        onClick={() => setSource('paste')}
                        style={{ ...styles.sourceTab, ...(source === 'paste' ? styles.sourceTabActive : {}) }}
                    >
                        <IoClipboardOutline size={15} />
                        <span>Paste rows</span>
                    </button>
                </div>

                {source === 'file' ? (
                    <>
                        <div
                            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                            onDragLeave={() => setDragActive(false)}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            style={{
                                ...styles.dropZone,
                                borderColor: dragActive ? Colors.PRM_LIGHT_BLUE : Colors.GRAY_MEDIUM,
                                background: dragActive ? Colors.PRM_BLUE_ACCENT : Colors.GRAY_LIGHT,
                            }}
                        >
                            <IoCloudUploadOutline size={36} color={Colors.PRM_LIGHT_BLUE} />
                            <p style={styles.dropTitle}>{file ? file.name : 'Drop your file here, or click to browse'}</p>
                            <p style={styles.dropSubtitle}>CSV or Excel (.xlsx)</p>
                        </div>
                        <p style={{ ...styles.helpText, marginTop: 12, marginBottom: 0 }}>
                            Working in Numbers or Google Sheets? Use <strong>Paste rows</strong> instead — no export needed.
                        </p>
                    </>
                ) : (
                    <>
                        <p style={styles.helpText}>
                            Select the passenger rows in Numbers, Excel or Google Sheets, copy them, and paste below.
                            The heading row is optional — without it the columns are read in template order.
                        </p>
                        <textarea
                            value={pastedText}
                            onChange={(e) => setPastedText(e.target.value)}
                            placeholder={'Paste your rows here…\n\nMary\tO\u2019Brien\tWCHR\tY\tY\tY\tEI\t712\t21/09/2026\tOutbound'}
                            rows={8}
                            style={styles.textarea}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
                            <Button
                                onClick={handlePaste}
                                disabled={!pastedText.trim()}
                                style={styles.secondaryButton}
                                variant="outline-primary"
                            >
                                Check Pasted Rows
                            </Button>
                            {pastedText.trim().length > 0 && (
                                <span style={{ ...styles.helpText, margin: 0 }}>
                                    {pastedText.trim().split(/\r\n|\n|\r/).length} line(s) pasted
                                </span>
                            )}
                        </div>
                    </>
                )}

                {parsing && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
                        <CustomSpinner />
                        <span style={styles.helpText}>Reading file…</span>
                    </div>
                )}

                {parseError.length > 0 && (
                    <div style={styles.errorBanner}>
                        <p style={styles.errorText}>{parseError}</p>
                    </div>
                )}

                {assumedColumnOrder && (
                    <div style={styles.warningBanner}>
                        <p style={styles.warningText}>
                            No heading row was found, so the columns were read in template order:{' '}
                            {BULK_TEMPLATE_COLUMNS.join(' · ')}. Check the preview below carefully.
                        </p>
                    </div>
                )}

                {unknownHeaders.length > 0 && (
                    <div style={styles.warningBanner}>
                        <p style={styles.warningText}>
                            <strong>Ignored column(s):</strong> {unknownHeaders.join(', ')}
                        </p>
                    </div>
                )}
            </div>

            {/* Step 3 — review */}
            {validation && (
                <div style={cardStyle}>
                    <div style={sectionHeader}>
                        <div style={stepBadge}>3</div>
                        <h3 style={sectionTitle}>Review</h3>
                    </div>

                    <div style={styles.statRow}>
                        <div style={{ ...styles.statCard, borderColor: Colors.GRAY_MEDIUM }}>
                            <span style={{ ...styles.statNumber, color: Colors.GRAY_DARK }}>{validation.rows.length}</span>
                            <span style={styles.statLabel}>Rows read</span>
                        </div>
                        <div style={{ ...styles.statCard, borderColor: Colors.DARK_GREEN }}>
                            <span style={{ ...styles.statNumber, color: Colors.DARK_GREEN }}>{validation.validCount}</span>
                            <span style={styles.statLabel}>Ready to create</span>
                        </div>
                        <div style={{ ...styles.statCard, borderColor: validation.errorCount > 0 ? Colors.DARK_RED : Colors.GRAY_MEDIUM }}>
                            <span style={{ ...styles.statNumber, color: validation.errorCount > 0 ? Colors.DARK_RED : Colors.GRAY_DARK }}>{validation.errorCount}</span>
                            <span style={styles.statLabel}>Need attention</span>
                        </div>
                    </div>

                    <div style={styles.tableWrap}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Row</th>
                                    <th style={styles.th}>Passenger</th>
                                    <th style={styles.th}>Assistance</th>
                                    <th style={styles.th}>Flight</th>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Direction</th>
                                    <th style={styles.th}>Origin</th>
                                    <th style={styles.th}>Dest.</th>
                                    <th style={styles.th}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {validation.rows.map((row) => (
                                    <tr key={row.__rowNumber} style={{ background: row.isValid ? '#fff' : Colors.LIGHT_RED }}>
                                        <td style={styles.td}>{row.__rowNumber}</td>
                                        <td style={styles.td}>{`${row.firstName} ${row.secondName}`.trim() || '—'}</td>
                                        <td style={styles.td}>{(row.assistance || []).join(', ') || '—'}</td>
                                        <td style={styles.td}>{`${row.carrierCode}${row.flightNumber}` || '—'}</td>
                                        <td style={styles.td}>{row.flightDate || '—'}</td>
                                        <td style={styles.td}>{row.flightType === 'INBOUND' ? 'Inbound' : row.flightType === 'OUTBOUND' ? 'Outbound' : '—'}</td>
                                        <td style={styles.td}>{row.originAssistance ? 'Y' : 'N'}</td>
                                        <td style={styles.td}>{row.destinationAssistance ? 'Y' : 'N'}</td>
                                        <td style={styles.td}>
                                            {row.isValid ? (
                                                <span style={{ color: Colors.DARK_GREEN, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                    <IoCheckmarkCircle size={14} /> Ready
                                                </span>
                                            ) : (
                                                <span style={{ color: Colors.DARK_RED, display: 'inline-flex', alignItems: 'flex-start', gap: 4 }}>
                                                    <IoAlertCircle size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                                                    <span>{Object.values(row.errors).join('; ')}</span>
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {validation.errorCount > 0 && (
                        <div style={styles.warningBanner}>
                            <p style={styles.warningText}>
                                Rows needing attention are skipped. {validation.validCount} request
                                {validation.validCount === 1 ? '' : 's'} will be created.
                            </p>
                        </div>
                    )}

                    <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Button
                            onClick={handleSubmit}
                            disabled={validation.validCount === 0 || uploadStatus === 'loading'}
                            style={styles.primaryButton}
                        >
                            {uploadStatus === 'loading'
                                ? <CustomSpinner />
                                : `Create ${validation.validCount} Request${validation.validCount === 1 ? '' : 's'}`}
                        </Button>
                        <Button variant="outline-secondary" onClick={resetAll} disabled={uploadStatus === 'loading'} style={styles.secondaryButton}>
                            Clear
                        </Button>
                    </div>

                    {uploadStatus === 'loading' && uploadProgress.total > 0 && (
                        <div style={{ marginTop: 16 }}>
                            <ProgressBar
                                now={Math.round((uploadProgress.completed / uploadProgress.total) * 100)}
                                label={`${uploadProgress.completed} / ${uploadProgress.total}`}
                            />
                            <p style={{ ...styles.helpText, marginTop: 8 }}>
                                Creating requests — please keep this page open.
                            </p>
                        </div>
                    )}

                    {uploadError && (
                        <div style={styles.errorBanner}>
                            <p style={styles.errorText}>{uploadError}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const styles = {
    helpText: {
        fontSize: 13,
        color: Colors.GRAY_DARK,
        margin: 0,
        marginBottom: 12,
        lineHeight: 1.5,
    },
    columnList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 16,
    },
    columnChip: {
        fontSize: 12,
        padding: '4px 10px',
        borderRadius: 20,
        background: Colors.PRM_BLUE_ACCENT,
        color: Colors.PRM_LIGHT_BLUE,
        fontWeight: 500,
    },
    optionalTag: {
        marginLeft: 6,
        fontSize: 10,
        color: Colors.GRAY_DARK,
        fontStyle: 'italic',
    },
    noteBanner: {
        marginTop: 16,
        padding: '12px 14px',
        background: Colors.PRM_BLUE_ACCENT,
        borderRadius: 8,
        borderLeft: `4px solid ${Colors.PRM_LIGHT_BLUE}`,
    },
    noteText: {
        color: Colors.TEXT_COLOR,
        margin: 0,
        fontSize: 13,
        lineHeight: 1.6,
    },
    sourceBar: {
        display: 'inline-flex',
        gap: 4,
        padding: 4,
        background: Colors.GRAY_LIGHT,
        borderRadius: 8,
        marginBottom: 16,
    },
    sourceTab: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '7px 14px',
        border: 'none',
        borderRadius: 6,
        background: 'transparent',
        color: Colors.GRAY_DARK,
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
    },
    sourceTabActive: {
        background: '#fff',
        color: Colors.PRM_LIGHT_BLUE,
        fontWeight: 600,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    },
    textarea: {
        width: '100%',
        padding: '12px 14px',
        border: `1px solid ${Colors.GRAY_MEDIUM}`,
        borderRadius: 8,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 12.5,
        lineHeight: 1.6,
        color: Colors.TEXT_COLOR,
        resize: 'vertical',
    },
    dropZone: {
        border: '2px dashed',
        borderRadius: 12,
        padding: '36px 20px',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
    },
    tipLine: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        margin: '14px 0 0',
        fontSize: 13,
        color: Colors.GRAY_DARK,
    },
    tipHint: {
        color: Colors.PRM_LIGHT_BLUE,
        fontWeight: 500,
        cursor: 'help',
        textDecoration: 'underline dotted',
        textUnderlineOffset: 3,
    },
    dropTitle: {
        margin: '12px 0 4px',
        fontSize: 14,
        fontWeight: 600,
        color: Colors.TEXT_COLOR,
    },
    dropSubtitle: {
        margin: 0,
        fontSize: 12,
        color: Colors.GRAY_DARK,
    },
    statRow: {
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        marginBottom: 20,
    },
    statCard: {
        flex: '1 1 140px',
        border: '1px solid',
        borderRadius: 10,
        padding: '14px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    statNumber: {
        fontSize: 24,
        fontWeight: 700,
        lineHeight: 1.1,
    },
    statLabel: {
        fontSize: 12,
        color: Colors.GRAY_DARK,
    },
    tableWrap: {
        maxHeight: 420,
        overflow: 'auto',
        border: `1px solid ${Colors.GRAY_LIGHT}`,
        borderRadius: 8,
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 13,
    },
    th: {
        position: 'sticky',
        top: 0,
        background: Colors.GRAY_LIGHT,
        padding: '10px 12px',
        textAlign: 'left',
        fontWeight: 600,
        fontSize: 12,
        color: Colors.GRAY_DARK,
        whiteSpace: 'nowrap',
        borderBottom: `1px solid ${Colors.GRAY_MEDIUM}`,
    },
    td: {
        padding: '8px 12px',
        borderBottom: `1px solid ${Colors.GRAY_LIGHT}`,
        verticalAlign: 'top',
    },
    primaryButton: {
        padding: '12px 32px',
        fontSize: 15,
        fontWeight: 600,
        borderRadius: 8,
        border: 'none',
        backgroundColor: Colors.PRM_LIGHT_BLUE,
        color: '#fff',
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(48, 127, 226, 0.3)',
    },
    secondaryButton: {
        padding: '10px 20px',
        fontSize: 14,
        fontWeight: 500,
        borderRadius: 8,
    },
    errorBanner: {
        marginTop: 12,
        padding: '10px 14px',
        background: Colors.LIGHT_RED,
        borderRadius: 8,
        borderLeft: `4px solid ${Colors.DARK_RED}`,
    },
    errorText: {
        color: Colors.DARK_RED,
        margin: 0,
        fontSize: 13,
        fontWeight: 500,
    },
    warningBanner: {
        marginTop: 12,
        padding: '10px 14px',
        background: '#FFF8E1',
        borderRadius: 8,
        borderLeft: '4px solid #F5A623',
    },
    warningText: {
        color: '#8A6100',
        margin: 0,
        fontSize: 13,
    },
};

export default BulkUploadForm;
